import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Users, Shield, Plus, Pencil, Trash2, RefreshCw, Crown, Cpu, KeyRound } from 'lucide-react';
import { puntovService } from '../../../services/api';
import FormularioUsuarioPuntoV from './FormularioUsuarioPuntoV';
import FormularioRolPuntoV from './FormularioRolPuntoV';

const PESTANAS = [
  { id: 'usuarios', texto: 'Usuarios', icono: Users },
  { id: 'roles', texto: 'Roles y permisos', icono: Shield }
];

const Estado = ({ activo, textoInactivo = 'Inactivo' }) => (activo
  ? <span className="inline-block px-2 py-0.5 rounded-full border border-green-200 bg-green-50 text-xs font-medium text-green-700">Activo</span>
  : <span className="inline-block px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 text-xs font-medium text-gray-500">{textoInactivo}</span>);

// Usuarios, roles y permisos de un cliente de Punto V, como en "Control de
// acceso" de Punto V Manager. Las reglas (dueño protegido, rol y máquinas de
// la misma empresa) las aplica Punto V; aquí solo se muestran sus errores.
const AccesosCliente = ({ cliente, maquinas, esAdmin }) => {
  const [pestana, setPestana] = useState('usuarios');
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permisos, setPermisos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [exito, setExito] = useState('');
  const [editandoUsuario, setEditandoUsuario] = useState(null);
  const [editandoRol, setEditandoRol] = useState(null);
  const [porEliminar, setPorEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const peticion = useRef(0);
  const cargar = useCallback(async () => {
    const mia = ++peticion.current;
    setCargando(true);
    setError('');
    try {
      const [u, r, p] = await Promise.all([
        puntovService.usuariosCliente(cliente.base_datos),
        puntovService.rolesCliente(cliente.base_datos),
        puntovService.permisosCliente(cliente.base_datos)
      ]);
      if (mia !== peticion.current) return;
      setUsuarios(u.data || []);
      setRoles(r.data || []);
      setPermisos(p.data || []);
    } catch (err) {
      if (mia === peticion.current) setError(err.message || 'Error al cargar los accesos del cliente');
    } finally {
      if (mia === peticion.current) setCargando(false);
    }
  }, [cliente.base_datos]);

  useEffect(() => {
    setExito('');
    setPorEliminar(null);
    cargar();
  }, [cargar]);

  // Si falla, el error se propaga al modal y se muestra ahí.
  const guardarUsuario = async (datos) => {
    const res = editandoUsuario === 'nuevo'
      ? await puntovService.crearUsuarioCliente(cliente.base_datos, datos)
      : await puntovService.actualizarUsuarioCliente(cliente.base_datos, editandoUsuario.id, datos);
    setEditandoUsuario(null);
    setExito(res.message);
    await cargar();
  };

  const guardarRol = async (datos) => {
    const res = editandoRol === 'nuevo'
      ? await puntovService.crearRolCliente(cliente.base_datos, datos)
      : await puntovService.actualizarRolCliente(cliente.base_datos, editandoRol.id, datos);
    setEditandoRol(null);
    setExito(res.message);
    await cargar();
  };

  const eliminarRol = async () => {
    setEliminando(true);
    setError('');
    try {
      const res = await puntovService.eliminarRolCliente(cliente.base_datos, porEliminar.id);
      setExito(res.message);
      setPorEliminar(null);
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el rol');
    } finally {
      setEliminando(false);
    }
  };

  const nombreRol = (u) => u.rol || (u.id_rol ? `Rol ${u.id_rol} (no existe)` : 'Sin rol');

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {editandoUsuario && (
        <FormularioUsuarioPuntoV
          cliente={cliente}
          usuario={editandoUsuario === 'nuevo' ? null : editandoUsuario}
          roles={roles}
          onGuardar={guardarUsuario}
          onCancel={() => setEditandoUsuario(null)}
        />
      )}
      {editandoRol && (
        <FormularioRolPuntoV
          cliente={cliente}
          rol={editandoRol === 'nuevo' ? null : editandoRol}
          permisos={permisos}
          maquinas={maquinas}
          onGuardar={guardarRol}
          onCancel={() => setEditandoRol(null)}
        />
      )}

      <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-2">
        <KeyRound size={16} className="text-gray-400" />
        <h3 className="font-semibold text-gray-800 text-sm">Accesos de {cliente.slug}</h3>
        <div role="tablist" aria-label="Accesos" className="flex gap-1 ml-2">
          {PESTANAS.map(({ id, texto, icono: Icono }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={pestana === id}
              onClick={() => setPestana(id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 ${
                pestana === id ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <Icono size={14} /> {texto}
              <span className="tabular-nums text-gray-400">{id === 'usuarios' ? usuarios.length : roles.length}</span>
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={cargar}
            disabled={cargando}
            aria-label="Actualizar accesos"
            className="p-1.5 border border-gray-300 text-gray-500 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={14} className={cargando ? 'animate-spin' : ''} />
          </button>
          {esAdmin && (
            <button
              type="button"
              onClick={() => { setExito(''); if (pestana === 'usuarios') setEditandoUsuario('nuevo'); else setEditandoRol('nuevo'); }}
              disabled={cargando || Boolean(error && !roles.length)}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1 text-xs font-medium disabled:opacity-50"
            >
              <Plus size={14} /> {pestana === 'usuarios' ? 'Nuevo usuario' : 'Nuevo rol'}
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        {exito && <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">{exito}</div>}
        {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        {pestana === 'usuarios' && (
          cargando && !usuarios.length ? <p className="py-8 text-center text-sm text-gray-400">Cargando usuarios…</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                    <th className="py-2 pr-3">Usuario</th>
                    <th className="py-2 pr-3">Rol</th>
                    <th className="py-2 pr-3">Teléfono / origen</th>
                    <th className="py-2 pr-3">Estado</th>
                    {esAdmin && <th className="py-2" />}
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map(u => (
                    <tr key={u.id} className="border-b border-gray-100">
                      <td className="py-2 pr-3">
                        <span className="flex items-center gap-1.5 font-medium text-gray-800">
                          {u.nombre_completo}
                          {u.es_dueno && <Crown size={13} className="text-amber-500" aria-label="Dueño de la empresa" />}
                        </span>
                        <span className="block text-xs text-gray-500">{u.correo}</span>
                      </td>
                      <td className="py-2 pr-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${u.rol ? 'bg-indigo-50 text-indigo-700' : 'bg-amber-50 text-amber-700'}`}>
                          {nombreRol(u)}
                        </span>
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs text-gray-600">{u.origin_number || '—'}</td>
                      <td className="py-2 pr-3"><Estado activo={u.activo} textoInactivo="Suspendido" /></td>
                      {esAdmin && (
                        <td className="py-2 text-right">
                          <button
                            type="button"
                            onClick={() => { setExito(''); setEditandoUsuario(u); }}
                            aria-label={`Editar ${u.correo}`}
                            title="Editar usuario"
                            className="p-1 text-gray-400 hover:text-blue-600"
                          >
                            <Pencil size={15} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {!cargando && !error && usuarios.length === 0 && (
                <p className="py-6 text-center text-sm text-gray-400">Este cliente no tiene usuarios</p>
              )}
            </div>
          )
        )}

        {pestana === 'roles' && (
          cargando && !roles.length ? <p className="py-8 text-center text-sm text-gray-400">Cargando roles…</p> : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {roles.map(r => (
                <div key={r.id} className={`rounded-xl border p-4 flex flex-col gap-2 min-w-0 ${r.activo ? 'border-gray-200' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-800 flex items-center gap-1.5">
                        <span className="truncate">{r.nombre}</span>
                        {r.es_del_dueno && <Crown size={13} className="text-amber-500 flex-shrink-0" aria-label="Rol del dueño" />}
                      </p>
                      <p className="text-xs text-gray-500 line-clamp-2">{r.descripcion || 'Sin descripción'}</p>
                    </div>
                    <Estado activo={r.activo} />
                  </div>
                  <p className="text-xs text-gray-600 flex flex-wrap gap-x-3 gap-y-1">
                    <span className="flex items-center gap-1"><Shield size={12} /> {r.permisos.length} permiso(s)</span>
                    <span className="flex items-center gap-1"><Cpu size={12} /> {r.maquinas.length} máquina(s)</span>
                    <span className="flex items-center gap-1"><Users size={12} /> {r.usuarios} usuario(s)</span>
                  </p>
                  {r.permisos.length === 0 && (
                    <p className="text-xs text-amber-700">Sin permisos: sus usuarios no ven nada en Punto V Manager.</p>
                  )}
                  {esAdmin && (
                    porEliminar?.id === r.id ? (
                      <div className="mt-auto flex items-center justify-end gap-2 pt-1 text-xs">
                        <span className="text-gray-600 mr-auto">¿Eliminar este rol?</span>
                        <button type="button" onClick={() => setPorEliminar(null)} disabled={eliminando} className="px-2 py-1 rounded hover:bg-gray-100 text-gray-600">No</button>
                        <button type="button" onClick={eliminarRol} disabled={eliminando} className="px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                          {eliminando ? 'Eliminando…' : 'Sí, eliminar'}
                        </button>
                      </div>
                    ) : (
                      <div className="mt-auto flex justify-end gap-1 pt-1">
                        <button
                          type="button"
                          onClick={() => { setExito(''); setEditandoRol(r); }}
                          className="px-2 py-1 rounded text-xs text-blue-600 hover:bg-blue-50 flex items-center gap-1"
                        >
                          <Pencil size={13} /> Permisos y máquinas
                        </button>
                        {!r.es_del_dueno && (
                          <button
                            type="button"
                            onClick={() => { setExito(''); setPorEliminar(r); }}
                            disabled={r.usuarios > 0}
                            title={r.usuarios > 0 ? 'Tiene usuarios: cámbiales el rol antes de eliminarlo' : 'Eliminar rol'}
                            aria-label={`Eliminar ${r.nombre}`}
                            className="p-1 rounded text-gray-400 hover:text-red-600 disabled:opacity-40 disabled:hover:text-gray-400"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    )
                  )}
                </div>
              ))}
              {!cargando && !error && roles.length === 0 && (
                <p className="py-6 text-center text-sm text-gray-400 md:col-span-2 xl:col-span-3">Este cliente no tiene roles</p>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default AccesosCliente;
